import { ArrowRight } from 'lucide-react'

const LOG_LEVEL_STYLES: Record<string, { bg: string; color: string; border: string; label: string }> = {
  info: {
    bg: 'rgba(59, 130, 246, 0.15)',
    color: '#60a5fa',
    border: 'rgba(59, 130, 246, 0.3)',
    label: 'Info',
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.15)',
    color: '#fbbf24',
    border: 'rgba(245, 158, 11, 0.3)',
    label: 'Warning',
  },
  error: {
    bg: 'rgba(239, 68, 68, 0.15)',
    color: '#f87171',
    border: 'rgba(239, 68, 68, 0.3)',
    label: 'Error',
  },
  debug: {
    bg: 'rgba(148, 163, 184, 0.15)',
    color: '#94a3b8',
    border: 'rgba(148, 163, 184, 0.3)',
    label: 'Debug',
  },
}

const ENTRY_BORDER_COLORS: Record<string, string> = {
  info: 'rgba(59, 130, 246, 0.15)',
  warning: 'rgba(245, 158, 11, 0.25)',
  error: 'rgba(239, 68, 68, 0.25)',
  debug: 'rgba(100, 180, 255, 0.1)',
}

function formatTimestamp(dateStr: string): { date: string; time: string } {
  const d = new Date(dateStr)
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  }
}

interface EAHandoverEntryProps {
  entry: Record<string, any>
  showProject?: boolean
  isLast?: boolean
}

export default function EAHandoverEntry({ entry, showProject = false, isLast = false }: EAHandoverEntryProps) {
  const logLevel = (entry.log_level || 'info').toLowerCase()
  const levelStyle = LOG_LEVEL_STYLES[logLevel] || LOG_LEVEL_STYLES.info
  const borderColor = ENTRY_BORDER_COLORS[logLevel] || ENTRY_BORDER_COLORS.info
  const ts = formatTimestamp(entry.created_at)

  const fromAgent = entry.from_agent || entry.agent_role || 'unknown'
  const toAgent = entry.to_agent || null
  const message = entry.message || entry.task_summary || ''
  const context = entry.context || null

  return (
    <div className="flex gap-4">
      {/* Timeline column */}
      <div className="flex flex-col items-center w-20 shrink-0">
        <div className="text-right w-full">
          <p className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            {ts.date}
          </p>
          <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {ts.time}
          </p>
        </div>
        {/* Timeline dot and line */}
        <div className="flex flex-col items-center mt-2">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{
              background: levelStyle.color,
              boxShadow: `0 0 8px ${levelStyle.color}66`,
            }}
          />
          {!isLast && (
            <div
              className="w-px flex-1 min-h-[24px]"
              style={{ background: 'rgba(100, 180, 255, 0.15)' }}
            />
          )}
        </div>
      </div>

      {/* Content card */}
      <div
        className="flex-1 rounded-xl p-4 mb-3 transition-all duration-200 hover:scale-[1.005]"
        style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: `1px solid ${borderColor}`,
        }}
      >
        {/* Top row: agents + log level */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span
              className="px-2.5 py-1 text-xs font-medium rounded-full"
              style={{
                background: 'rgba(139, 92, 246, 0.15)',
                color: '#a78bfa',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              }}
            >
              {fromAgent}
            </span>
            {toAgent && (
              <>
                <ArrowRight size={14} style={{ color: 'rgba(148, 163, 184, 0.5)' }} />
                <span
                  className="px-2.5 py-1 text-xs font-medium rounded-full"
                  style={{
                    background: 'rgba(6, 182, 212, 0.15)',
                    color: '#22d3ee',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                  }}
                >
                  {toAgent}
                </span>
              </>
            )}
          </div>
          <span
            className="px-2.5 py-1 text-xs font-medium rounded-full"
            style={{
              background: levelStyle.bg,
              color: levelStyle.color,
              border: `1px solid ${levelStyle.border}`,
            }}
          >
            {levelStyle.label}
          </span>
        </div>

        {/* Project link (global log only) */}
        {showProject && entry.ea_project_id && (
          <a
            href={`/ea/projects/${entry.ea_project_id}`}
            className="text-xs font-medium mb-2 inline-block transition-colors hover:underline"
            style={{ color: '#60a5fa' }}
          >
            Project: {entry.project_name || entry.ea_project_id}
          </a>
        )}

        {/* Message */}
        <p className="text-sm leading-relaxed" style={{ color: '#e2e8f0' }}>
          {message}
        </p>

        {/* Context JSON (if present, show collapsed) */}
        {context && typeof context === 'object' && Object.keys(context).length > 0 && (
          <details className="mt-2">
            <summary
              className="text-xs cursor-pointer select-none"
              style={{ color: 'rgba(148, 163, 184, 0.6)' }}
            >
              Context details
            </summary>
            <pre
              className="mt-1 text-xs rounded-lg p-3 overflow-x-auto"
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                color: 'rgba(148, 163, 184, 0.8)',
                border: '1px solid rgba(100, 180, 255, 0.1)',
              }}
            >
              {JSON.stringify(context, null, 2)}
            </pre>
          </details>
        )}

        {/* Additional fields: action_taken, status, risk_rating, notes, vendor */}
        <div className="flex items-center flex-wrap gap-2 mt-2">
          {entry.vendor && (
            <span
              className="px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                background: 'rgba(59, 130, 246, 0.15)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}
            >
              {entry.vendor}
            </span>
          )}
          {entry.status && (
            <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
              Status: {entry.status}
            </span>
          )}
          {entry.risk_rating && (
            <span className="text-xs" style={{ color: 'rgba(245, 158, 11, 0.8)' }}>
              Risk: {entry.risk_rating}
            </span>
          )}
        </div>

        {entry.action_taken && (
          <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            Action: {entry.action_taken}
          </p>
        )}

        {entry.notes && (
          <p className="text-xs mt-1 italic" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {entry.notes}
          </p>
        )}
      </div>
    </div>
  )
}
